import { Component, ElementRef, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ConsumerHistoryModel } from '../../models/consumer/consumer-history.model';
import { ConsumerTimelineModel } from '../../models/consumer/consumer-timeline.model';
import { ConsumerModel } from '../../models/consumer/consumer.model';
import { HistoryCreditStatusModel } from '../../models/consumer/history-credit-status.model';
import { LatestBalanceModel } from '../../models/consumer/latest-balance.model';
import { ConsumerService } from '../../services/consumer.service';
import { ConsumerInteractionService } from '../../services/consumer-interaction.service';
import { ClaimsService } from '../../services/claims.service';
import { navItemKey } from '../../models/consumer/consumer-interaction.model';
import swal from 'sweetalert2';

enum deviceStatus {
    Unavailable = "UNAVAILABLE",
    Connecting = "CONNECTING",
    Connected = "CONNECTED"
};

enum interactionType {
    GetMeterRead = "Get Meter Read",
    ResetCreditStatus = "Reset Credit Status",
    CreditStatusAdjustment = "Credit Status Adjustment",
    GetCreditStatus = "Get Credit Status",
    GetTarrif = "Get Tarrif",
    SetTarrif = "Set Tarrif",
    SetStandingCharge = "Set Standing Charge",
    GetFDSettings = "Get FD Settings",
    ChangeFDSettings = "Change FD Settings",
    SendScreenMessage = "Send Screen Message"
};

@Component({
    selector: 'consumer-detail',
    templateUrl: './consumer-detail.component.html',
    styleUrls: ['./consumer-detail.component.scss']
})
export class ConsumerDetailComponent implements OnInit, OnDestroy {

    public subscriptions: any[] = [];
    public latestBalances: LatestBalanceModel[] = [];
    public consumerModeTimeline: ConsumerHistoryModel[] = [];
    public creditStatuses: HistoryCreditStatusModel[] = [];

    consumerId: string;
    clientId: number;
    isSearching: boolean = false;
    consumerHasPayg: boolean = false;
    consumerHasBilling: boolean = false;

    isSuperUser: boolean = false;
    isPayg: boolean = false;
    isPaygUpdate: boolean = false;
    isBilling: boolean = false;
    isConsumption: boolean = false;
    isProcessing: boolean = false;

    navigationList: any[] = [];

    selectedNavItem: navItemKey = navItemKey.Account;
    consumer: ConsumerModel = new ConsumerModel();
    consumerTimeLine: ConsumerTimelineModel[] = [];
    consumerIssues: any[] = [];
    consumerIssuesStrings: string[] = [];
    latestBalance: LatestBalanceModel = new LatestBalanceModel();
    initialSearchPerformed: boolean = false;
    issuesTabOpen: boolean = false;
    deviceStatus: string = deviceStatus.Connecting;
    timelineColours: string[] = [
        '#00539f',
        '#009aa3',
        '#93c01f',
        '#153954',
        '#F9E533',
        '#310A31',
        '#f46036'
    ];

    @ViewChild('timePeriod', { static: true }) timePeriod: ElementRef;
    accountBalanceIssues: any[];
    billIssues: any[];
    creditStatusIssues: any[];
    meterConsumptionIssues: any[];
    paymentsIssues: any[];

    constructor(
        private consumerService: ConsumerService,
        private consumerInteractionService: ConsumerInteractionService,
        private claimsService: ClaimsService,
        private route: ActivatedRoute,
        private router: Router,
        private _location: Location) { }

    ngOnInit() {
        this.consumerId = this.route.snapshot.params['consumerId'];
        this.clientId = this.route.parent.snapshot.params['clientId'];

        this.getRequestTypeFromStorage();

        try {
            //claim check for USER
            this.isSuperUser = this.claimsService.isSuperUser();
            this.isPayg = this.claimsService.hasPaygFeature();
            this.isPaygUpdate = this.claimsService.hasPaygUpdateFeature();
            this.isBilling = this.claimsService.hasBillingFeature();
            this.isConsumption = this.claimsService.hasConsumptionFeature();
        } catch {
        }

        if (this.consumerId === null || this.consumerId === undefined) {
            this.router.navigate(['/portal', this.clientId, 'consumers']);
            return;
        }

        this.consumerService.getConsumer(this.clientId, this.consumerId).then(result => {
            this.consumer = result;

            this.consumerHasPayg = (this.consumer.consumerType === 'PAYG') ? true : false;
            this.consumerHasBilling = (this.consumer.consumerType === 'Billing') ? true : false;

            //get device status after consumer claims has been figured out
            if(this.consumerHasPayg) {
                this.consumerInteractionService.getDeviceStatus(this.clientId, this.consumerId).then((deviceRes) => {
                    this.deviceStatus = deviceRes.connectionState.toUpperCase();
                }).catch((error) => {
                    this.deviceStatus = deviceStatus.Unavailable;
                });
            }

            this.createOrUpdateNavList();
        });

        this.consumerService.getConsumerIssues(this.clientId, this.consumerId).then(
            result => {
                this.consumerIssues = result;
            });

        let timePeriod = 1;
        if (this.route.snapshot.queryParams['timePeriod']) {
            this.timePeriod.nativeElement.value =
                parseFloat(this.route.snapshot.queryParams['timePeriod']);
        } else {
            this.update(timePeriod);
        }

        this.consumerService.getConsumerHistory(this.clientId, this.consumerId).then(result => {
            this.consumerModeTimeline = result;
            let totalDays: number = 1;

            if (this.consumerModeTimeline.length < 1) return;

            let lastItem = this.consumerModeTimeline[this.consumerModeTimeline.length - 1];
            if (lastItem.recordToDate) {
                totalDays = this.getDifferenceInDays(this.consumerModeTimeline[0].recordFromDate, this.consumerModeTimeline[this.consumerModeTimeline.length - 1].recordToDate);
            } else {
                totalDays = this.getDifferenceInDays(this.consumerModeTimeline[0].recordFromDate, new Date());
            }

            for (let i = 0; i < this.consumerModeTimeline.length; i++) {
                let fromDate = this.consumerModeTimeline[i].recordFromDate;
                let toDate = this.consumerModeTimeline[i].recordToDate;
                let days = this.getDifferenceInDays(fromDate, toDate);
                let percentage = ((days / totalDays) * 100).toString() + '%';

                if (i == this.consumerModeTimeline.length - 1) {
                    this.consumerTimeLine.push({
                        recordToDate: toDate,
                        recordFromDate: fromDate,
                        precentage: percentage,
                        colour: this.timelineColours[i],
                        consumerType: this.consumerModeTimeline[i].consumerType
                    });
                } else {
                    this.consumerTimeLine.push({
                        recordFromDate: fromDate,
                        precentage: percentage,
                        colour: this.timelineColours[i],
                        consumerType: this.consumerModeTimeline[i].consumerType
                    });
                }
            }
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    toggleIssuesTab() {
        this.issuesTabOpen = !this.issuesTabOpen;
    }

    navigateBack() {
        this._location.back();
    }

    getRequestTypeFromStorage() {
        let requestType = window.sessionStorage.getItem("RequestType");
        if (requestType) {
            if (requestType == interactionType.CreditStatusAdjustment) {
                this.toggleConsumerDetail(navItemKey.Account);
            }

            if (requestType == interactionType.GetMeterRead
                || requestType == interactionType.GetTarrif
                || requestType == interactionType.SetTarrif
                || requestType == interactionType.SetStandingCharge) {
                this.toggleConsumerDetail(navItemKey.TarrifConsumption);
            }

            if (requestType == interactionType.GetFDSettings || requestType == interactionType.ChangeFDSettings ||
                requestType == interactionType.SendScreenMessage) {
                this.toggleConsumerDetail(navItemKey.Device);
            }
            window.sessionStorage.removeItem("RequestType");
        }
    }

    onDeviceStatusRefresh() {

        this.deviceStatus = deviceStatus.Connecting;
        this.isProcessing = true;
        this.consumerInteractionService.getDeviceStatus(this.clientId, this.consumerId).then((deviceRes) => {
            this.deviceStatus = deviceRes.connectionState.toUpperCase();
            this.isProcessing = false;
        }).catch((error) => {
            this.deviceStatus = deviceStatus.Unavailable;
            this.isProcessing = false;
        });
    }

    connectionStatusInfo() {

        swal({
            title: 'Connection status',
            html: '<p style="text-align: left;">This is identifying whether the PAYG device is currently connected to the network.</p>\
             <p style="text-align: left;"> Where the device is connected it is more likely that the device will receive and complete interactions, however, where disconnected this means the device is off-line, interactions will likely not complete.</p>',
            confirmButtonText: 'Close',
            confirmButtonColor: '#00539f'
        });

    }

    getDifferenceInDays(fromDate: Date, toDate: Date): number {
        let toDateTime = toDate ? new Date(toDate) : new Date();
        let fromDateTime = new Date(fromDate);
        return Math.abs(toDateTime.getTime() - fromDateTime.getTime()) / (1000 * 60 * 60 * 24);
    }

    update(value: any) {
        let queryParams: any = JSON.parse(JSON.stringify(this.route.snapshot.queryParams)) as any;
        queryParams.timePeriod = +value;

        if (this.consumerId === undefined) {
            this.router.navigate(['/portal', this.clientId, 'consumers'], { queryParams: queryParams, replaceUrl: true });
        } else {
            this.router.navigate(['/portal', this.clientId, 'consumer', this.consumerId], { queryParams: queryParams, replaceUrl: true });
        }
    }

    createOrUpdateNavList() {
        let component = this.router.routerState.snapshot.url.split('/');

        if (this.navigationList.length == 0) {
            this.navigationList.push(
                {
                    key: navItemKey.Account,
                    label: "Account",
                    isShown: true
                },
                {
                    key: navItemKey.TarrifConsumption,
                    label: "Tariff & Consumption",
                    isShown: true
                }
                //,{
                //    key: navItemKey.Device,
                //    label: "Device",
                //    isShown: this.consumerHasPayg
                //}
            )
        }
    }

    toggleConsumerDetail(item: navItemKey) {
        this.selectedNavItem = item;
    }
}
